import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./account.css";
import whygreneLogo from "../../assets/images/WG_4dot_1.png";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useMsal } from "@azure/msal-react";
import {
  popupLoginRequest,
  redirectLoginRequest,
  silentRequest,
} from "../../msalconfig";

import { debugAuthSnapshot } from "../../utils/debugAuthSnapshot";
import { clearLocalAuthState } from "../../utils/clearLocalAuthState";

import { createUserSession } from "../../session/UserSessionModel";
import { useUserSession } from "../../session/useUserSession";

import { PYTHON_API_BASE_URL } from "../../services/api/apiConstants";
import { useNavigation } from "../../contexts/NavigationContext";

export default function Login() {
  const [authError, setAuthError] = useState("");
  const AUTH_IN_PROGRESS_KEY = "wg_auth_in_progress";
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const pythonBackendUrl = process.env.REACT_APP_PYTHON_BACKEND_URL;

  const inferEnvLabel = (url) => {
    const v = String(url || "").toLowerCase();
    if (!v) return "unset";
    if (v.includes("testing") || v.includes("test")) return "testing";
    if (v.includes("prod") || v.includes("production")) return "prod";
    return "unknown";
  };
  // Start with loading=true if we detect MSAL redirect parameters in the URL
  const isMsalRedirect = window.location.hash.includes("state=") && 
                         (window.location.hash.includes("code=") || window.location.hash.includes("error="));
  const [isLoading, setIsLoading] = useState(isMsalRedirect);
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { setUserSession } = useUserSession();
  const { clearNavigation, setSelectedBranch, setSelectedCustomer } = useNavigation();
  const devLoginWorkaroundRawPrimary = process.env.REACT_APP_DEV_LOGIN_WORKAROUND;
  const devLoginWorkaroundRawLegacy =
    process.env.REACT_APP_DEV_LOGIN_WORKAROUND_LEGACY;
  // Prefer the new flag when it is set to a non-empty value; otherwise fall back to legacy.
  // (docker-compose may set the new flag to "" when DEV_LOGIN_WORKAROUND is unset)
  const devLoginWorkaroundRaw =
    devLoginWorkaroundRawPrimary && String(devLoginWorkaroundRawPrimary).trim() !== ""
      ? devLoginWorkaroundRawPrimary
      : devLoginWorkaroundRawLegacy;
  const devLoginWorkaroundEnabled = (() => {
    const raw = devLoginWorkaroundRaw;
    if (raw == null) return false;
    const normalized = String(raw).trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  })();


  // ----------------------------------------------------------------------------
  // Build and store user session
  // ----------------------------------------------------------------------------
  const buildAndStoreUserSession = useCallback(async ({
    account,
    accessToken,
    idToken,
    expirationTime,
    idTokenClaims,
  }) => {
    const loginTime = Date.now();
    const userDetails = await fetch(`${PYTHON_API_BASE_URL}/users/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!userDetails.ok) {
      const body = await userDetails.text();
      throw new Error(
        `GET /users/ failed: ${userDetails.status} ${userDetails.statusText} — ${body}`,
      );
    }

    const userData = await userDetails.json();
    const userSession = createUserSession({
      account,
      accessToken,
      idToken: idToken || "",
      expirationTime,
      loginTime,
      idTokenClaims,
      userData,
    });

    setUserSession(userSession);

    if (userData?.id) {
      sessionStorage.setItem("UserID", String(userData.id));
    }
    if (userData?.organization_id) {
      sessionStorage.setItem("organizationId", String(userData.organization_id));
    }
    if (userData?.organization_name) {
      sessionStorage.setItem("organizationName", userData.organization_name);
    }

    return { userData, userSession, loginTime };
  }, [setUserSession]);






  // IMPORTANT:
  // Do NOT clear sessionStorage here. MSAL uses web storage for temporary auth state
  // during redirect flows; clearing it can break handleRedirectPromise().

  const resetLocalLoginState = useCallback(() => {
    setUserSession(null);
    clearNavigation();
    clearLocalAuthState();
  }, [clearNavigation, setUserSession]);

  useEffect(() => {
    const processRedirect = async () => {
      try {
        // If we initiated login (redirect/popup) in this tab, keep the user on a
        // "completing sign-in" state across the full-page redirect.
        try {
          if (sessionStorage.getItem(AUTH_IN_PROGRESS_KEY) === "1") {
            setIsLoading(true);
          }
        } catch {
          // ignore
        }

        // React.StrictMode (dev) can cause duplicate effect execution and even
        // duplicate MSAL redirect handling, which can clear/reset storage twice
        // and produce UI flicker in slower automation contexts.
        if (window.__wgAuthRedirectProcessing || window.__wgAuthRedirectProcessed) {
          debugAuthSnapshot("LoginPage:processRedirect:skipped_duplicate", {
            processing: !!window.__wgAuthRedirectProcessing,
            processed: !!window.__wgAuthRedirectProcessed,
          });
          return;
        }
        window.__wgAuthRedirectProcessing = true;

        debugAuthSnapshot("LoginPage:processRedirect:start");
        const response = await instance.handleRedirectPromise();
        if (response) {
          window.__wgAuthRedirectProcessed = true;
          debugAuthSnapshot("LoginPage:processRedirect:response", {
            hasAccessToken: !!response.accessToken,
          });
          const account = response.account;
          instance.setActiveAccount(account);
          const { idTokenClaims } = response;
          const expirationTime = idTokenClaims?.exp * 1000; // Convert to milliseconds

          const email = idTokenClaims?.email || idTokenClaims?.emails?.[0];
          const jobTitle = idTokenClaims?.jobTitle;
          sessionStorage.setItem("jobTitle", jobTitle);
          sessionStorage.setItem("email", email);
          // Ensure we have an API access token before navigating to protected routes.
          let accessToken = response.accessToken;
          if (!accessToken) {
            try {
              debugAuthSnapshot("LoginPage:processRedirect:acquireTokenSilent:start");
              const tokenRes = await instance.acquireTokenSilent({
                ...silentRequest,
                account,
              });
              accessToken = tokenRes.accessToken;
              debugAuthSnapshot("LoginPage:processRedirect:acquireTokenSilent:ok", {
                accessTokenLen: accessToken?.length || 0,
              });
            } catch (e) {
              console.warn(
                "MSAL redirect succeeded but acquireTokenSilent failed; staying on login.",
                e,
              );
              debugAuthSnapshot("LoginPage:processRedirect:acquireTokenSilent:fail", {
                error: String(e?.message || e),
              });
            }
          }

          if (!accessToken) {
            setAuthError(
              "Signed in, but couldn't get an access token yet. Please try again.",
            );
            setIsLoading(false);
            debugAuthSnapshot("LoginPage:processRedirect:no_access_token");
            return;
          }

          sessionStorage.setItem("accessToken", accessToken);
          sessionStorage.setItem(
            "tokenExpiration",
            expirationTime?.toString?.() || "",
          );
          const { userData, userSession, loginTime } =
            await buildAndStoreUserSession({
              account,
              accessToken,
              idToken: response.idToken,
              expirationTime,
              idTokenClaims,
            });
          sessionStorage.setItem("loginTime", loginTime.toString());
          sessionStorage.setItem("idToken", response.idToken || "");
          /* REVIEW (Jon): these logs print credential material to the browser
           * console in production builds. Six sites in this file - here, and
           * again at the acquireTokenSilent and non-redirect paths below.
           * userSession carries idToken; tokenRes at the silent-acquire site
           * carries a live accessToken. debugAuthSnapshot() beside them already
           * does the safe thing, logging accessTokenLen instead of the token,
           * so the pattern to copy is right there. Left in place rather than
           * removed because it predates this branch and deleting logging is a
           * behaviour change that wants its own commit. */
          console.log("User details fetched:", userData);
          console.log("User session created:", userSession);

          if (userData.branch_id) {
            setSelectedBranch({
              id: userData.branch_id,
              branch_name: userData.branch_name,
            });
          }

          // Navigate immediately after tokens are processed
          setIsLoading(false);
          try {
            sessionStorage.removeItem(AUTH_IN_PROGRESS_KEY);
          } catch {
            // ignore
          }

          if (jobTitle === "Organization Admin") {
            debugAuthSnapshot("LoginPage:navigate", { to: "/branchhomepage" });
            navigate("/branchhomepage");
          } else if (jobTitle === "Branch Admin") {
            debugAuthSnapshot("LoginPage:navigate", { to: "/customer-projects" });
            navigate("/customer-projects");
          } else if (jobTitle === "Employee") {
            debugAuthSnapshot("LoginPage:navigate", { to: "/customer-projects" });
            navigate("/customer-projects");
          } else if (jobTitle === "Customer") {
            setSelectedCustomer(email, idTokenClaims?.given_name);
            debugAuthSnapshot("LoginPage:navigate", { to: "/microgrid-page-design" });
            navigate("/microgrid-page-design");
          } else {
            debugAuthSnapshot("LoginPage:navigate", { to: "/employeehomepage" });
            navigate("/employeehomepage");
          }
        } else {
          setIsLoading(false);
          try {
            sessionStorage.removeItem(AUTH_IN_PROGRESS_KEY);
          } catch {
            // ignore
          }
        }
      } catch (error) {
        console.error("Error handling redirect:", error);
        setAuthError(
          "An error occurred during authentication. Please try again.",
        );
        debugAuthSnapshot("LoginPage:processRedirect:error", {
          error: String(error?.message || error),
        });
        try {
          sessionStorage.removeItem(AUTH_IN_PROGRESS_KEY);
        } catch {
          // ignore
        }
        // Allow retry if something went wrong
        window.__wgAuthRedirectProcessed = false;
      }
      finally {
        window.__wgAuthRedirectProcessing = false;
      }
    };

    processRedirect();
  }, [AUTH_IN_PROGRESS_KEY, buildAndStoreUserSession, instance, navigate, resetLocalLoginState, setSelectedBranch, setSelectedCustomer]);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      resetLocalLoginState();
      try {
        sessionStorage.setItem(AUTH_IN_PROGRESS_KEY, "1");
      } catch {
        // ignore
      }
      debugAuthSnapshot("LoginPage:handleLogin:start", {
        devLoginWorkaroundEnabled,
      });
      if (devLoginWorkaroundEnabled) {
        // Dev-only: avoid popup windows (often blocked) by using redirect flow.
        await instance.loginRedirect(redirectLoginRequest);
        return; // navigation happens via redirect + handleRedirectPromise()
      }
      const loginResponse = await instance.loginPopup(popupLoginRequest);
      const account = loginResponse.account;
      instance.setActiveAccount(account);
      const { idTokenClaims } = loginResponse;
      const expirationTime = idTokenClaims?.exp * 1000; // Convert to milliseconds
      
      // MSAL may not always return an API access token from loginPopup (depends on policy/scopes).
      // Always follow-up with an explicit token acquisition for the API scopes we need.
      let accessToken = loginResponse.accessToken;
      if (!accessToken) {

        console.log("No access token found, acquiring token silently");
        // If the access token is not found, we need to acquire a new token silently
        try {
          debugAuthSnapshot("LoginPage:handleLogin:acquireTokenSilent:start");
          const tokenRes = await instance.acquireTokenSilent({
            ...silentRequest,
            account,
          });
          /* REVIEW (Jon): tokenRes.accessToken is a live bearer token and this
           * prints the whole object. See the note at the first logging site
           * above. */
          console.log("Token acquired silently", tokenRes);
          accessToken = tokenRes.accessToken;
          debugAuthSnapshot("LoginPage:handleLogin:acquireTokenSilent:ok", {
            accessTokenLen: accessToken?.length || 0,
          });
        } catch (e) {
          console.warn(
            "MSAL login succeeded but acquireTokenSilent failed; API calls may be unauthorized.",
            e,
          );
          debugAuthSnapshot("LoginPage:handleLogin:acquireTokenSilent:fail", {
            error: String(e?.message || e),
          });
        }
      }

      if (!accessToken) {
        // If the access token is not found, we need to throw an error
        console.warn(
          "MSAL did not return an access token. Check B2C app/API permissions and requested scopes.",
          {
            scopes: popupLoginRequest.scopes,
            authority: instance.getActiveAccount()?.environment,
          },
        );
        setAuthError(
          "Signed in, but couldn't get an access token yet. Please try again.",
        );
        setIsLoading(false);
        debugAuthSnapshot("LoginPage:handleLogin:no_access_token");
        return;
      }

      const email = idTokenClaims?.email || idTokenClaims?.emails?.[0];
      const jobTitle = idTokenClaims?.jobTitle;  // Make sure this claim exists in B2C
      sessionStorage.setItem("jobTitle", jobTitle);
      sessionStorage.setItem("email", email); 
      sessionStorage.setItem("accessToken", accessToken || "");
      sessionStorage.setItem("tokenExpiration", expirationTime.toString()); // Store expiration time
      const { userData, userSession, loginTime } =
        await buildAndStoreUserSession({
          account,
          accessToken,
          idToken: loginResponse.idToken,
          expirationTime,
          idTokenClaims,
        });
      sessionStorage.setItem("loginTime", loginTime.toString());
      sessionStorage.setItem("idToken", loginResponse.idToken);
      console.log("User details fetched:", userData);
      console.log("User session created:", userSession);

      if (userData.branch_id) {
        setSelectedBranch({
          id: userData.branch_id,
          branch_name: userData.branch_name,
        });
      }

      setIsLoading(false);
      try {
        sessionStorage.removeItem(AUTH_IN_PROGRESS_KEY);
      } catch {
        // ignore
      }
      if (jobTitle === "Organization Admin") {
        debugAuthSnapshot("LoginPage:navigate", { to: "/branchhomepage" });
        navigate("/branchhomepage");
      } else if (jobTitle === "Branch Admin") {
        debugAuthSnapshot("LoginPage:navigate", { to: "/customer-projects" });
        navigate("/customer-projects");
      } else if (jobTitle === "Employee") {
        debugAuthSnapshot("LoginPage:navigate", { to: "/customer-projects" });
        navigate("/customer-projects");
      } else if (jobTitle === "Customer") {
        setSelectedCustomer(email, idTokenClaims?.given_name);
        debugAuthSnapshot("LoginPage:navigate", { to: "/microgrid-page-design" });
        navigate("/microgrid-page-design");
      } else {
        debugAuthSnapshot("LoginPage:navigate", { to: "/employeehomepage" });
        navigate("/employeehomepage");
      }
      
    } catch (error) {
      console.error("Login failed:", error);
      setAuthError("Authentication failed. Please try again.");
      setIsLoading(false);
      try {
        sessionStorage.removeItem(AUTH_IN_PROGRESS_KEY);
      } catch {
        // ignore
      }
      debugAuthSnapshot("LoginPage:handleLogin:error", {
        error: String(error?.message || error),
      });
    }
  };

  // Show a clean loading state when completing sign-in.
  // The in-progress flag prevents a brief flash of the login form after the OAuth redirect,
  // especially when the hash is quickly cleared by MSAL before React renders.
  const isAuthInProgress = (() => {
    try {
      return sessionStorage.getItem(AUTH_IN_PROGRESS_KEY) === "1";
    } catch {
      return false;
    }
  })();
  if ((isMsalRedirect && isLoading) || isAuthInProgress) {
    return (
      <div className="account-container">
        <div className="account-right">
          <div className="account-form" style={{ textAlign: "center" }}>
            <img
              src={whygreneLogo}
              alt="WHYgrene Logo"
              className="account-whygrene-logo"
            />
            <h2>Welcome to Digital Twin</h2>
            <p className="heading-l4" style={{ marginTop: 24 }}>
              Completing sign-in, please wait...
            </p>
            <div style={{ marginTop: 24 }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 32, color: "#4CAF50" }}></i>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="account-container">
      <div className="account-right">
        <div className="account-form">
          <img
            src={whygreneLogo}
            alt="WHYgrene Logo"
            className="account-whygrene-logo"
          />
          <h2>Welcome to Digital Twin</h2>
          <p className="heading-l2">Your Gateway to </p>
          <span className="heading-l3">Smarter Energy Management</span>
          <p className="heading-l4">
            Login and start transforming your energy landscape today{" "}
          </p>
          {process.env.NODE_ENV === "development" ? (
            <div className="heading-l4" style={{ opacity: 0.7, marginTop: 6 }}>
              <div>
                Dev login redirect: <b>{devLoginWorkaroundEnabled ? "ON" : "OFF"}</b> {" "}

              </div>
              <div style={{ marginTop: 4 }}>
                Simulate API ({inferEnvLabel(baseUrl)}):{" "}
                <span style={{ opacity: 0.85 }}>
                  <b>{baseUrl ?? "unset"}</b>  
                </span>
              </div>
              <div style={{ marginTop: 2 }}>
                Admin API ({inferEnvLabel(pythonBackendUrl)}):{" "}
                <span style={{ opacity: 0.85 }}>
                  <b>{pythonBackendUrl ?? "unset"}</b>
                </span>
              </div>
            </div>
          ) : null}

          {authError && (
            <div className="account-error-message">
              <i className="fas fa-exclamation-circle"></i>
              <span>{authError}</span>
            </div>
          )}

          <div className="account-form-group account-password-group">
           
           
          </div>

          <div className="account-form-group account-password-group">
            {/* <label className="account-label">Password</label> */}
            <div className="account-password-container">
              
            </div>
          </div>

          <div className="account-button-group">
            <button
              className="account-submit-button"
              onClick={handleLogin}
              aria-label="Login with your account"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </button>
           
          </div>
           <div className="account-signup-group">
             Don't have an account?

            <Link to="/register" className="account-signup-link">
              Sign Up
            </Link>
           </div>
           

        </div>
      </div>
    </div>
  );
}
